import { TuitionListResponse } from '../../types/periodTuition';
import {
  BulkRowError,
  CohortRange,
  CohortSetting,
  ContinuityPaymentSettings,
  ContinuityPaymentSettingsSaveBody,
  PeriodAndSchoolResponse,
  TariffListResponse,
  TcTariffSubmitData,
} from '../../types/tuitionContinuityTrariff';
import { TUITION_CONTINUITY_TARIFF } from '../endpoints';
import request from '../request';
import { Period } from './MantainerPortal';

/**
 * Obtiene el listado de matrículas con paginación
 */
export const getTuitionContinuityValuesRequest = (
  items: number = 10,
  page: number = 0,
) => {
  const url = `${TUITION_CONTINUITY_TARIFF.TUITION_LIST}`;
  return request<TuitionListResponse>(url, {
    params: { items_per_page: items, page },
  });
};

export const getTcPeriodsValue = () =>
  request<Period[]>(TUITION_CONTINUITY_TARIFF.PERIODS, {
    method: 'get',
  });

export const saveTuitionContinuityValue = (data: {
  periodId: number;
  value: number;
  id?: number;
}) => {
  const url = `${TUITION_CONTINUITY_TARIFF.SAVE_TUITION_VALUE}${
    data.id ? '/' + data.id : ''
  }`;
  return request(url, { method: data.id ? 'put' : 'post', data });
};

export const getPeriodAndSchool = () =>
  request<{ data: PeriodAndSchoolResponse }>(
    TUITION_CONTINUITY_TARIFF.GET_PERIOD_AND_OFFER,
    { method: 'get' },
  );

export const getTcTariffList = (
  periodId: number | string,
  schoolId: number | string,
) =>
  request<{ data: TariffListResponse[] }>(
    TUITION_CONTINUITY_TARIFF.GET_TARIFF_LIST,
    {
      method: 'get',
      params: { periodId, schoolId },
    },
  );

export const saveTcTariffList = (data: TcTariffSubmitData[]) =>
  request(TUITION_CONTINUITY_TARIFF.SAVE_TARIFFS, {
    method: 'post',
    data: { tariffList: data },
  });

/**
 * Obtiene el flag "Ingresos por rango de cohorte" para un período + escuela.
 */
export const getCohortSetting = (periodId: number, schoolId: number) =>
  request<CohortSetting>(TUITION_CONTINUITY_TARIFF.COHORT_SETTING, {
    method: 'get',
    params: { periodId, schoolId },
  });

/**
 * Activa o desactiva los ingresos por rango de cohorte (período + escuela).
 */
export const saveCohortSetting = (
  periodId: number,
  schoolId: number,
  enabled: boolean,
) =>
  request(TUITION_CONTINUITY_TARIFF.COHORT_SETTING, {
    method: 'put',
    data: { periodId, schoolId, enabled },
  });

/**
 * Lista los rangos de cohorte COMPARTIDOS de un período + escuela.
 */
export const getCohortRanges = (periodId: number, schoolId: number) =>
  request<CohortRange[]>(TUITION_CONTINUITY_TARIFF.COHORT_RANGES, {
    method: 'get',
    params: { periodId, schoolId },
  });

/**
 * Agrega un rango de cohorte compartido al set del período + escuela.
 */
export const addCohortRange = (
  periodId: number,
  schoolId: number,
  fromYear: number,
  toYear: number,
) =>
  request(TUITION_CONTINUITY_TARIFF.COHORT_RANGES, {
    method: 'post',
    data: { periodId, schoolId, fromYear, toYear },
  });

/**
 * Modifica la duración (Desde-Hasta) de un rango de cohorte compartido por id.
 * Afecta a todos los planes/ofertas del período + escuela.
 */
export const updateCohortRange = (
  id: number,
  fromYear: number,
  toYear: number,
) =>
  request(`${TUITION_CONTINUITY_TARIFF.COHORT_RANGES}/${id}`, {
    method: 'put',
    data: { fromYear, toYear },
  });

/**
 * Elimina un rango de cohorte compartido por id (cascada a sus celdas).
 */
export const deleteCohortRange = (id: number) =>
  request(`${TUITION_CONTINUITY_TARIFF.COHORT_RANGES}/${id}`, {
    method: 'delete',
  });

/**
 * Guarda el precio de una celda (oferta × rango).
 */
export const saveCohortCell = (data: {
  // Si la oferta aún no tiene offer-period, enviar offerId + periodId
  // y el backend lo resuelve/crea.
  curriculumOfferPeriodId?: number;
  offerId?: number;
  periodId?: number;
  cohortRangeId: number;
  value: number;
  tuition?: number;
  credit?: number;
}) =>
  request(TUITION_CONTINUITY_TARIFF.COHORT_CELLS, {
    method: 'post',
    data,
  });

/**
 * Elimina una celda de precio de cohorte por id.
 */
export const deleteCohortCell = (id: number) =>
  request(`${TUITION_CONTINUITY_TARIFF.COHORT_CELLS}/${id}`, {
    method: 'delete',
  });

/**
 * Descarga la plantilla (prellenada) para carga masiva de aranceles continuidad.
 * Los labels (capa de idioma) viajan como query para los encabezados.
 */
export const downloadCohortRangesTemplate = (
  periodId: number,
  schoolId: number,
  labels: {
    inscriptionLabel: string;
    tuitionLabel: string;
    creditLabel: string;
  },
) =>
  request(TUITION_CONTINUITY_TARIFF.COHORT_RANGES_TEMPLATE, {
    responseType: 'blob',
    params: { periodId, schoolId, ...labels },
  });

/**
 * Carga masiva (upsert por fila) de aranceles continuidad.
 */
export const uploadCohortRangesTemplate = (
  periodId: number,
  schoolId: number,
  file: File,
) => {
  const formData = new FormData();
  formData.append('file', file);
  return request<{ updatedRows: number; errors: BulkRowError[] }>(
    TUITION_CONTINUITY_TARIFF.COHORT_RANGES_UPLOAD,
    {
      method: 'post',
      data: formData,
      params: { periodId, schoolId },
    },
  );
};

/**
 * Obtiene la configuración de pago (máximo de cuotas, día de pago y regla de
 * primera cuota) del calendario de continuidad de un período. Todo `null`
 * cuando aún no hay fila guardada; 404 cuando el período no tiene calendario
 * de continuidad (no todos los períodos de `getPeriodAndSchool` tienen uno).
 */
export const getContinuityPaymentSettings = (periodId: number) =>
  request<ContinuityPaymentSettings>(TUITION_CONTINUITY_TARIFF.PAYMENT_SETTINGS, {
    method: 'get',
    params: { periodId },
  });

/**
 * Guarda la configuración de pago de continuidad. Semántica de actualización
 * parcial: un campo omitido no se toca; `null` explícito limpia
 * maxInstallments/payday. Ver `ContinuityPaymentSettingsSaveBody`.
 */
export const saveContinuityPaymentSettings = (
  data: ContinuityPaymentSettingsSaveBody,
) =>
  request(TUITION_CONTINUITY_TARIFF.PAYMENT_SETTINGS, {
    method: 'put',
    data,
  });
